import React, {createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {
    ActiveFormData,
    AutoFillFieldInfo,
    AutoFillResponse,
    FieldInfoResponse,
    FormPermissions,
    FormSubmissionGeneralFileMeta,
    GeneralFile,
    INFO_TYPE,
    ReportFormMetaData,
} from "../../api/ApiTypes.ts";
import {UseFormReturn} from "react-hook-form";
import {FormComponent, FormSchema, FormValues} from "./DynamicFormTypes.ts";
import {FileDownloadType} from "../FileViewer.tsx";
import {useFileApi, useFormApi} from "../../context/ApiContext.tsx";
import {handleDownload, handleView} from "../../filehelper/fileutils.ts";
import {NotificationType, useNotification} from "../../context/NotificationContext.tsx";
import {AggregateValidator} from "./fields/customvalidator/AggregateValidator.ts";
import {SUBMIT_MODE} from "./dyanimcFormUtil.tsx";
import equal from "fast-deep-equal";


// 1. Static Context - Never changes after mount
interface FormStaticContextValue {
    metaData: ReportFormMetaData;
    schema: FormSchema;
    defaultValues: FormValues;
    flattenedSchemaMap: Map<string, FormComponent>;
    formPermissions: FormPermissions;
    customValidator: AggregateValidator | undefined;
}

// 2. Methods Context - Rarely changes
interface FormMethodsContextValue {
    formMethods: UseFormReturn<FormValues>;
    triggerValidation: () => Promise<void>;
}

// 3. Files Context - Changes frequently
interface FormFilesContextValue {
    generalFiles: GeneralFile[];
    addGeneralFile: (file: File, fileMeta: FormSubmissionGeneralFileMeta) => Promise<GeneralFile | undefined>;
    removeGeneralFile: (fileId: number) => Promise<void>;
    downloadFile: (fileId: number, type: FileDownloadType) => Promise<void>;
    viewFile: (fileId: number, type: FileDownloadType) => Promise<void>;
}

// 4. Actions Context - Never changes after mount
interface FormActionsContextValue {
    saveProgress: () => Promise<void>;
    clearProgress: () => Promise<void>;
    getActiveFormData: () => ActiveFormData;
}

interface AppliedAutoFillUpdate {
    fieldKey: string;
    value: string;
}

interface ApplyAutoFillOptions {
    triggerValidation?: boolean;
    skipFieldKeysFor?: string[];
    setValueOptions?: {
        shouldValidate?: boolean;
        shouldDirty?: boolean;
        shouldTouch?: boolean;
    };
}


// 5. Info Context - Changes occasionally
interface FormInfoContextValue {
    setInfo: (fieldKey: string, info: FieldInfoResponse) => void;
    setAutoFillInfos: (infos: Record<string, AutoFillFieldInfo>) => void;
    applyAutoFillResponse: (response: AutoFillResponse, applyOptions: ApplyAutoFillOptions) => Promise<AppliedAutoFillUpdate[]>;
    getInfo: (fieldKey: string) => Record<string, FieldInfoResponse> | undefined;
    clearInfo: (fieldKey: string, infoType: INFO_TYPE) => void;
    clearDataGridRowInfos: (afterIndex: number, dataGridKey: string) => void;
    clearDataGridRowInfosAndMoveSubsequentRows: (afterIndex: number, dataGridKey: string) => void;
    setHidden: (parentFieldKey: string) => void;
    removeHidden: (parentFieldKey: string) => void;
}

const FormStaticContext = createContext<FormStaticContextValue | undefined>(undefined);
const FormMethodsContext = createContext<FormMethodsContextValue | undefined>(undefined);
const FormFilesContext = createContext<FormFilesContextValue | undefined>(undefined);
const FormActionsContext = createContext<FormActionsContextValue | undefined>(undefined);
const FormInfoContext = createContext<FormInfoContextValue | undefined>(undefined);

interface DyanmicFormProviderProps {
    children: ReactNode,
    metaData: ReportFormMetaData,
    formMethods: UseFormReturn<FormValues>
    schema: FormSchema,
    defaultValues: FormValues,
    triggerValidationUpdate: () => Promise<void>;
    flattenedSchemaMap: Map<string, FormComponent>;
    formPermissions: FormPermissions
    customValidator: AggregateValidator | undefined;
}

export const DynamicFormProvider: React.FC<DyanmicFormProviderProps> = ({
                                                                            children,
                                                                            metaData,
                                                                            formMethods,
                                                                            schema,
                                                                            defaultValues,
                                                                            triggerValidationUpdate,
                                                                            flattenedSchemaMap,
                                                                            formPermissions,
                                                                            customValidator = undefined,
                                                                        }) => {

    // Get API hooks
    const fileApi = useFileApi();
    const formApi = useFormApi();
    const { showNotification } = useNotification();



    // State
    const [generalFiles, setGeneralFiles] = useState<GeneralFile[]>(() => {
        return metaData.generalFiles || [];
    });

    // Use refs for values that callbacks need but don't trigger re-renders
    const metaDataRef = useRef(metaData);
    const formMethodsRef = useRef(formMethods);
    const formPermissionsRef = useRef(formPermissions);
    const schemaRef = useRef(schema);
    const triggerValidationUpdateRef = useRef(triggerValidationUpdate);

    // Update refs when props change
    useEffect(() => {
        metaDataRef.current = metaData;
        formMethodsRef.current = formMethods;
        formPermissionsRef.current = formPermissions;
        schemaRef.current = schema;
        triggerValidationUpdateRef.current = triggerValidationUpdate;
    }, [metaData, formMethods, formPermissions, schema, triggerValidationUpdate]);

    // Callbacks with proper dependencies
    const addGeneralFile = useCallback(async (file: File, fileMeta: FormSubmissionGeneralFileMeta) => {
        try {
            const newGeneralFile = await fileApi.uploadFormSubmissionFile({
                file: file,
                fileMeta: fileMeta,
                formMeta: metaDataRef.current
            });

            setGeneralFiles(prev => {
                return [...prev, newGeneralFile];
            });
            return newGeneralFile;
        } catch (error) {
            console.error('Error uploading file:', error);
            return undefined;
        }
    }, [fileApi]);

    const removeGeneralFile = useCallback(async (fileId: number) => {
        try {
            await fileApi.deleteFormSubmissionFile(fileId);
            setGeneralFiles(prev => {
                return prev.filter(file => file.id !== fileId);
            });
        } catch (error) {
            console.error('Error removing file:', error);
        }
    }, [fileApi]);

    const downloadFile = useCallback(async (fileId: number, type: FileDownloadType) => {
        try {
            const download = await fileApi.downloadFile(fileId, type);
            await handleDownload(download);
        } catch (error) {
            console.error('Failed to download file:', error);
        }
    }, [fileApi]);

    const viewFile = useCallback(async (fileId: number, type: FileDownloadType) => {
        try {
            const download = await fileApi.downloadFile(fileId, type);
            await handleView(download);
        } catch (error) {
            console.error('Failed to view file:', error);
        }
    }, [fileApi]);

    const cachedProgress: FormValues | null = useRef(null);

    const saveProgress = useCallback(async () => {
        if (!formMethodsRef.current) return;

        try {
            // If user is admin, or allowEdits is false, do not save progress
            if (!formPermissionsRef.current.canSave) {
                if (formPermissionsRef.current.submitMode === SUBMIT_MODE.QC) {
                    console.log("not saving", formPermissionsRef.current);
                    return;
                }
                const message = "Saving of progress not allowed in submit mode: " + formPermissionsRef.current.submitMode;
                console.log(message, formPermissionsRef.current);
                showNotification(NotificationType.SUCCESS, message);
                return;
            }
            const currentValues = formMethodsRef.current.getValues();

            // Compare with cached values
            if (cachedProgress.current !== null) {
                const hasChanges = !equal(currentValues, cachedProgress.current);

                if (!hasChanges) {
                    showNotification(NotificationType.INFO, "No changes detected");
                    return;
                }
            }

            // Save to API
            await formApi.saveProgress(
                metaDataRef.current.formDefinitionId,
                metaDataRef.current.submissionId,
                metaDataRef.current.orderId,
                currentValues
            );

            cachedProgress.current = structuredClone(currentValues);

            showNotification(NotificationType.SUCCESS, "Saved Current Progress");
        }
        catch (error) {
            showNotification(NotificationType.ERROR, "Error saving progress");
            console.error('Error saving progress:', error);
        }
    }, [formApi, showNotification]);

    const getActiveFormData = useCallback((): ActiveFormData => {
        return {
            formSchema: schemaRef.current,
            metaData: metaDataRef.current,
            values: formMethodsRef.current.getValues(),
        };
    }, []);

    const clearProgress = useCallback(async () => {
        if (!formMethodsRef.current) return;
        if (!formPermissionsRef.current.canSave) {
            const message = "Clearing of progress not allowed in submit mode: " + formPermissionsRef.current.submitMode;
            console.log(message, formPermissionsRef.current);
            showNotification(NotificationType.SUCCESS, message);
            return;
        }
        try {
            await formApi.saveProgress(
                metaDataRef.current.formDefinitionId,
                metaDataRef.current.submissionId,
                metaDataRef.current.orderId,
                {}
            );
            showNotification(NotificationType.SUCCESS, "Cleared Current Progress");
            window.location.reload();
        }
        catch (error) {
            showNotification(NotificationType.ERROR, "Error Clearing Progress");
            console.error('Error saving progress:', error);
        }
    }, [formApi, showNotification]);

// ===== INFO OPERATIONS =====
    function isRecord(value: unknown): value is Record<string, FieldInfoResponse> {
        return value !== null && typeof value === "object" && !Array.isArray(value);
    }

    const setInfo = useCallback((fieldKey: string, info: FieldInfoResponse) => {
            if (!formMethodsRef.current) {
                console.log("no form methods");
                return;
            }
            if (!info.type) {
                info.type = INFO_TYPE.DEFAULT;
            }

            const convertedFieldKey = `_info.${fieldKey.replace(/\./g, "_")}`;

            const rawValue = formMethodsRef.current.getValues(convertedFieldKey);

            const oldInfos: Record<string, FieldInfoResponse> = isRecord(rawValue)
                ? rawValue
                : {};

            const key = info.type;

            const updatedInfos = {
                ...oldInfos,
                [key]: info,
            };

            formMethodsRef.current.setValue(convertedFieldKey, updatedInfos);
        },
        [formMethodsRef]
    );

    const setAutoFillInfos = useCallback((infos: Record<string, AutoFillFieldInfo>) => {
            if (!formMethodsRef.current) {
                return undefined;
            }
            if (!infos || Object.keys(infos).length === 0) {
                return undefined;
            }

            const currentInfo = formMethodsRef.current.getValues("_info") ?? {};
            const updatedInfos = { ...currentInfo };

            for (const [fieldKey, info] of Object.entries(infos ?? {})) {
                const convertedFieldKey = fieldKey.replace(/\./g, "_");
                const existing = isRecord(updatedInfos[convertedFieldKey]) ? updatedInfos[convertedFieldKey] : {};
                updatedInfos[convertedFieldKey] = {
                    ...existing,
                    [INFO_TYPE.PROVIDED]: { type: INFO_TYPE.PROVIDED, title: "Provided:", content: info?.message },
                };
            }

            formMethodsRef.current.setValue("_info", updatedInfos);
        },
        [formMethodsRef]
    );



    interface SimpleFieldUpdate {
        fieldKey: string;
        value: string;
    }

    interface GridFieldUpdate {
        index: number;
        subFieldKey: string;
        value: string;
    }


    const defaultApplyAutoFillOptions: Required<ApplyAutoFillOptions> = {
        triggerValidation: false,
        skipFieldKeysFor: [],
        setValueOptions: {
            shouldValidate: false,
            shouldDirty: false,
            shouldTouch: false,
        },
    };



    const applyAutoFillResponse = useCallback(async (
        response: AutoFillResponse,
        applyOptions: ApplyAutoFillOptions = {}
    ): Promise<AppliedAutoFillUpdate[]> => {

        if (!formMethodsRef.current || !response?.values) {
            return []
        }
        const {
            triggerValidation,
            skipFieldKeysFor,
            setValueOptions,
        } = {
            ...defaultApplyAutoFillOptions,
            ...applyOptions,
            setValueOptions: {
                ...defaultApplyAutoFillOptions.setValueOptions,
                ...applyOptions.setValueOptions,
            },
        };

        const simpleUpdates: SimpleFieldUpdate[] = [];
        const gridUpdates = new Map<string, GridFieldUpdate[]>();
        const appliedUpdates: AppliedAutoFillUpdate[] = [];
        const skipFieldKeys = new Set(skipFieldKeysFor);

        console.log("Applying auto fill response:", response, "options", applyOptions, "triggerValidation", triggerValidation, "skipFieldKeysFor", skipFieldKeysFor, "setValueOptions", setValueOptions);

        //Go through the autofill values, split into dg updates of simple updates
        for (const [fieldKey, value] of Object.entries(response.values)) {
            if (!fieldKey || skipFieldKeys.has(fieldKey)) {
                continue; //skip watched field keys (no infinite loop)
            }
            //if it has no dot its not formatted like a grid key update (gridkey.index.subGridkey)
            if (!fieldKey.includes(".")) {
                simpleUpdates.push({ fieldKey, value });
                continue;
            }
            /* fieldKey = "comparables.0.salePrice"
               split result:
               gridKey = "comparables" index = "0" subFieldKey = "salePrice"
             */
            const [gridKey, index, subFieldKey] = fieldKey.split(".");
            const rowIndex = Number(index);

            if (!gridKey || !subFieldKey || Number.isNaN(rowIndex)) {
                console.warn("Skipping malformed auto-fill field key:", fieldKey);
                continue;
            }

            const updatesForGrid = gridUpdates.get(gridKey) ?? [];

            updatesForGrid.push({ index: rowIndex, subFieldKey, value });
            gridUpdates.set(gridKey, updatesForGrid);
        }

        // #2 apply simple field updates
        for (const { fieldKey, value } of simpleUpdates) {
            formMethodsRef.current.setValue(fieldKey, value, setValueOptions);
            appliedUpdates.push({ fieldKey, value });
        }

        // #3 apply data-grid updates (one setValue per grid key)
        for (const [gridKey, updates] of gridUpdates) {
            const currentRows = formMethodsRef.current.getValues(gridKey) || [];
            //clone so we dont accidently mutate
            const nextRows = [...currentRows];

            for (const { index, subFieldKey, value } of updates) {
                /**
                 * Existing row example:
                 *
                 * nextRows[0] = {
                 *   address: "123 Main St",
                 *   salePrice: ""
                 * }
                 *
                 * Update:
                 * nextRows[0] = {
                 *   ...nextRows[0],
                 *   salePrice: "300000"
                 * }
                 * If nextRows[index] is undefined, spreading still safely creates
                 * a new row object with only the updated subKey.
                 */
                nextRows[index] = {
                    ...nextRows[index],
                    [subFieldKey]: value }
                ;
                appliedUpdates.push({
                    fieldKey: `${gridKey}.${index}.${subFieldKey}`,
                    value,
                });
            }
            formMethodsRef.current.setValue(gridKey, nextRows, setValueOptions);
        }
        console.log("Set form values from auto fill response:", response.values);

        const nextInfos = { ...(response.infos ?? {}) };
        //strip the skipkey out of infos as well
        for (const fieldKey of skipFieldKeys) {
            delete nextInfos[fieldKey];
        }

        setAutoFillInfos(nextInfos);

        console.log("Set auto fill infos from response:", response.infos);

        if (triggerValidation && appliedUpdates.length > 0) {
            await triggerValidationUpdateRef.current();
            console.log("Finished trigger validation after applying auto fill response");
        }
        return appliedUpdates;
    }, [setAutoFillInfos]);

    const getInfo = useCallback((fieldKey: string): Record<string, FieldInfoResponse> | undefined => {
        if (!formMethodsRef.current) return undefined;
        const convertedFieldKey = `_info.${fieldKey.replace(/\./g, "_")}`;
        const rawValue = formMethodsRef.current.getValues(convertedFieldKey);
        return isRecord(rawValue) ? rawValue as Record<string, FieldInfoResponse> : undefined;
    }, [formMethodsRef]);

    const clearInfo = useCallback((fieldKey: string, infoType: INFO_TYPE) => {
        if (!formMethodsRef.current) return;
        const convertedFieldKey = `_info.${fieldKey.replace(/\./g, "_")}`;
        const rawValue = formMethodsRef.current.getValues(convertedFieldKey);
        const oldInfos: Record<string, FieldInfoResponse> = isRecord(rawValue) ? rawValue : {};
        const updatedInfos = {...oldInfos};
        delete updatedInfos[infoType];
        formMethodsRef.current.setValue(convertedFieldKey, updatedInfos);
    }, [formMethodsRef]);

    const clearDataGridRowInfos = useCallback((deletedIndex: number, dataGridKey: string) => {
        if (!formMethodsRef.current) return;
        const currentInfo = formMethodsRef.current.getValues("_info");
        if (!isRecord(currentInfo)) return;

        const prefix = `${dataGridKey}_${deletedIndex}_`;
        const newInfo = Object.fromEntries(
            Object.entries(currentInfo).filter(([k]) => !k.startsWith(prefix))
        );
        formMethodsRef.current.setValue("_info", newInfo);

    }, [formMethodsRef]);

    const clearDataGridRowInfosAndMoveSubsequentRows = useCallback((deletedIndex: number, dataGridKey: string) => {
        if (!formMethodsRef.current) return;
        const currentInfo = formMethodsRef.current.getValues("_info");
        if (!isRecord(currentInfo)) return;

        const convertedDataGridKey = dataGridKey.replace(/\./g, "_");
        const gridPrefix = `${convertedDataGridKey}_`;
        const dropPrefix = `${convertedDataGridKey}_${deletedIndex}_`;
        const newInfo: Record<string, FieldInfoResponse> = {};
        for (const [fieldKey, info] of Object.entries(currentInfo)) {
            // Drop the deleted row
            if (fieldKey.startsWith(dropPrefix)) continue;
            // Same datagrid — might need to shift index down
            if (fieldKey.startsWith(gridPrefix)) {
                const remainder = fieldKey.slice(gridPrefix.length);
                const us = remainder.indexOf("_");
                const index = Number(us === -1 ? remainder : remainder.slice(0, us));
                if (Number.isInteger(index) && index > deletedIndex) {
                    const rest = us === -1 ? "" : remainder.slice(us);
                    newInfo[`${gridPrefix}${index - 1}${rest}`] = info as FieldInfoResponse;
                    continue;
                }
                // else: not an indexed key, or index < deletedIndex → fall through and keep as-is
            }
            newInfo[fieldKey] = info as FieldInfoResponse;
        }
        formMethodsRef.current.setValue("_info", newInfo);
    }, [formMethodsRef]);

// ===== HIDDEN OPERATIONS =====
    const setHidden = useCallback((parentFieldKey: string) => {
        if (!formMethodsRef.current) return;
        const hiddenKey = `_hidden.${parentFieldKey.replace(/\./g, "_")}`;
        formMethodsRef.current.setValue(hiddenKey, {});
    }, [formMethodsRef]);

    const removeHidden = useCallback((parentFieldKey: string) => {
        if (!formMethodsRef.current) return;
        const hiddenKey = `_hidden.${parentFieldKey.replace(/\./g, "_")}`;
        formMethodsRef.current.setValue(hiddenKey, undefined);
    }, [formMethodsRef]);

    // Create context values - memoize separately
    const staticContextValue = useMemo(() => {
        return {
            metaData,
            schema,
            defaultValues,
            flattenedSchemaMap,
            formPermissions,
            customValidator
        };
    }, [metaData, schema, defaultValues, flattenedSchemaMap, formPermissions, customValidator]);

    const methodsContextValue = useMemo(() => {
        return {
            formMethods,
            triggerValidation: triggerValidationUpdate
        };
    }, [formMethods, triggerValidationUpdate]);

    const filesContextValue = useMemo(() => {
        return {
            generalFiles,
            addGeneralFile,
            removeGeneralFile,
            downloadFile,
            viewFile
        };
    }, [generalFiles, addGeneralFile, removeGeneralFile, downloadFile, viewFile]);

    const actionsContextValue = useMemo(() => {
        return {
            saveProgress,
            clearProgress,
            getActiveFormData
        };
    }, [saveProgress, clearProgress, getActiveFormData]);

    const infoContextValue = useMemo(() => {
        return {
            setInfo,
            setAutoFillInfos,
            applyAutoFillResponse,
            getInfo,
            clearInfo,
            clearDataGridRowInfos,
            clearDataGridRowInfosAndMoveSubsequentRows,
            setHidden,
            removeHidden,
        };
    }, [setInfo, setAutoFillInfos, applyAutoFillResponse, getInfo, clearInfo, clearDataGridRowInfos, clearDataGridRowInfosAndMoveSubsequentRows, setHidden, removeHidden]);

    return (
        <FormStaticContext.Provider value={staticContextValue}>
            <FormMethodsContext.Provider value={methodsContextValue}>
                <FormFilesContext.Provider value={filesContextValue}>
                    <FormActionsContext.Provider value={actionsContextValue}>
                        <FormInfoContext.Provider value={infoContextValue}>
                            {children}
                        </FormInfoContext.Provider>
                    </FormActionsContext.Provider>
                </FormFilesContext.Provider>
            </FormMethodsContext.Provider>
        </FormStaticContext.Provider>
    );
};

// Add logging to hooks
export const useFormStatic = () => {
    const context = useContext(FormStaticContext);
    if (!context) throw new Error("useFormStatic must be used within DynamicFormProvider");
    return context;
};

export const useFormMethods = () => {
    const context = useContext(FormMethodsContext);
    if (!context) throw new Error("useFormMethods must be used within DynamicFormProvider");
    return context;
};

export const useFormFiles = () => {
    const context = useContext(FormFilesContext);
    if (!context) throw new Error("useFormFiles must be used within DynamicFormProvider");
    return context;
};

export const useFormActions = () => {
    const context = useContext(FormActionsContext);
    if (!context) throw new Error("useFormActions must be used within DynamicFormProvider");
    return context;
};

export const useFormInfo = () => {
    const context = useContext(FormInfoContext);
    if (!context) throw new Error("useFormInfo must be used within FormInfoProvider");
    return context;
};

